import type { IncomingHttpHeaders } from 'node:http';
import type { CartCountResponse, ExhibitionListInput, ExhibitionListResponse } from '@weberus/shared-types';

import type { QueryClient } from '@tanstack/react-query';
import type { FetcherType } from '@/app/_shared/utils/fetcher/types';
import { createQueryKeys } from '@lukemorales/query-key-factory';
import { commonPrefetchUpdater } from '@/app/_shared/utils/react-query-helper';

/**
 * 유저 cart count 조회
 */
export const fetchUserCartCount = (fetcher: FetcherType) => async (headers?: IncomingHttpHeaders) => {
  return fetcher<CartCountResponse>({
    url: '/journey/cart/count',
    method: 'GET',
    headers: headers as Record<string, string>,
  });
};

/**
 * 전시회 리스트 조회 API
 */
export const getExhibitionList =
  (fetcher: FetcherType) => async (args: ExhibitionListInput, headers?: IncomingHttpHeaders) => {
    return fetcher<ExhibitionListResponse, ExhibitionListInput>({
      url: `/display-site/list/exhibition`,
      method: 'GET',
      params: args,
      headers: headers as Record<string, string>,
    });
  };

export const exhibitionListKeys = (fetcher: FetcherType) =>
  createQueryKeys('exhibition-list', {
    list: (args: ExhibitionListInput, headers?: IncomingHttpHeaders) => ({
      queryKey: [args],
      queryFn: ({ pageParam = 1 }) => {
        return getExhibitionList(fetcher)(
          {
            page: pageParam,
            ...(args.exhibitionCategory ? { exhibitionCategory: args.exhibitionCategory } : {}),
            ...(args.limit ? { limit: args.limit } : {}),
          },
          headers,
        );
      },
    }),
  });

// -------------- Query --------------

export const prefetchExhibitionListPageQuery =
  (fetcher: FetcherType) =>
  async (queryClient: QueryClient, args: ExhibitionListInput, headers?: IncomingHttpHeaders) => {
    const { queryKey, queryFn } = exhibitionListKeys(fetcher).list(args, headers);

    return queryClient
      .prefetchInfiniteQuery({
        queryKey,
        queryFn,
        initialPageParam: 1,
      })
      .then(() => {
        queryClient.setQueryData(queryKey, commonPrefetchUpdater);
      });
  };
